import { requestFailure } from '@/api'

/**
 * What the Tools page says when the MCP server refuses to let this app register, or cannot be reached, while an OAuth
 * sign-in starts (#1936). The API answers each with 400 and a code; a refused registration also carries the status
 * the other server answered as `upstreamStatus`, which picks the wording. The copy names the server and what it did,
 * and never the code: the failure is the other server's, not ours, and the person can act on it.
 *
 * The same words are shown in Standard and Advanced. An HTTP status is not an error code (#1749), so a status the
 * copy has no words of its own for is named.
 */

/**
 * The name the copy gives the server: the display name typed, or an Express tool's name, else the host of its MCP
 * URL (`mcp.figma.com`), which is always there and says which server it was.
 */
export function oauthServerName(displayName: string, mcpUrl: string): string {
    const named = displayName.trim()
    if (named) {
        return named
    }
    try {
        return new URL(mcpUrl.trim()).hostname || 'The MCP server'
    } catch {
        return 'The MCP server'
    }
}

/** Where the sign-in was started from, which decides what the person can do next. */
export type OAuthStartContext = {
    /**
     * An Express tool's sign-in: its address and its OAuth app are the platform's, and the form shows neither a URL
     * nor a client id to change, so the copy never asks the person to change them.
     */
    express?: boolean
}

/** The next step for an Express tool, whose sign-in settings are managed for the person. */
const EXPRESS_NEXT =
    "This Express tool's sign-in settings are managed for you, so there is nothing to change here. Try again later."

/** The next step for a person who already has an OAuth app at the server. It names the form's own field. */
function enterOwnClientId(server: string): string {
    return `Enter the OAuth client id of an OAuth app you created at ${server}.`
}

/**
 * What the server did with the registration, chosen by the status it answered, and the next step for a person who
 * can bring their own OAuth app. The words name the form's own field, "OAuth client id", so it is easy to find.
 */
function registrationRefused(server: string, upstreamStatus: number | undefined): { what: string; next: string } {
    const ownApp = enterOwnClientId(server)
    const newApp = `create an OAuth app at ${server}, add the redirect URI shown below, and enter its OAuth client id.`
    if (upstreamStatus === undefined) {
        // The API refuses before sending anything when the registration address is not a public one.
        return {
            what: `${server} asks apps to register at an address that is not on the public internet, so this app cannot register there.`,
            next: ownApp,
        }
    }
    if (upstreamStatus === 401 || upstreamStatus === 403) {
        return { what: `${server} did not allow this app to register.`, next: `To connect it, ${newApp}` }
    }
    if (upstreamStatus === 400) {
        return { what: `${server} rejected this app's registration request.`, next: `To connect it, ${newApp}` }
    }
    if (upstreamStatus >= 500 && upstreamStatus <= 599) {
        return {
            what: `${server} had a problem registering this app (HTTP ${upstreamStatus}).`,
            next: `Try again later, or enter the OAuth client id of an OAuth app you created at ${server}.`,
        }
    }
    return { what: `${server} refused to register this app (HTTP ${upstreamStatus}).`, next: ownApp }
}

/**
 * The copy for an OAuth start the other server refused or never answered, or null when the failure is not one of
 * those: the screen keeps its own words for the rest.
 */
export function oauthStartRefusalMessage(
    error: unknown,
    server: string,
    context: OAuthStartContext = {},
): string | null {
    if (!(error instanceof Error)) {
        return null
    }
    const express = context.express === true
    switch (error.message) {
        case 'oauth_registration_refused': {
            const { what, next } = registrationRefused(server, requestFailure(error)?.upstreamStatus)
            return `${what} ${express ? EXPRESS_NEXT : next}`
        }
        case 'dcr_failed': {
            const next = express ? EXPRESS_NEXT : enterOwnClientId(server)
            return `${server} registered this app but sent back no client id. ${next}`
        }
        case 'oauth_server_unreachable':
            return express
                ? `Could not reach ${server} to start sign-in. Try again in a moment.`
                : `Could not reach ${server} to start sign-in. Check the MCP server URL and try again.`
        default:
            return null
    }
}
